import { useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Archive, RotateCcw, Trash2, Loader2 } from "lucide-react";
import { resetDeletedBadgeCache } from "@/components/catalog/DiscontinuedBadge";

function ImpactList({ info }) {
  return (
    <div className="space-y-3 text-sm" data-testid="lifecycle-impact">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Removed from these active areas</p>
        <p className="mt-1 text-charcoal">{info.active_surfaces.join(" · ")}</p>
      </div>
      {info.variants.length > 1 && <p className="text-xs text-warmgrey">Variants affected: {info.variants.map((v) => v.sku).join(", ")}</p>}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Historical records (kept)</p>
        {info.history.length ? (
          <ul className="mt-1 list-disc pl-5 text-charcoal" data-testid="lifecycle-history">{info.history.map((h) => <li key={h.collection}>{h.area}: {h.count}</li>)}</ul>
        ) : <p className="mt-1 text-charcoal" data-testid="lifecycle-history-none">None found</p>}
      </div>
      {info.config.length > 0 && <p className="text-xs text-warmgrey" data-testid="lifecycle-config">Pricing settings: {info.config.map((c) => `${c.area} (${c.count})`).join(", ")} — kept on archive, removed on permanent delete.</p>}
      {info.commitments?.length > 0 && (
        <div className="rounded-md border border-status-reserved/50 bg-status-reserved/10 p-3" data-testid="lifecycle-commitments">
          <p className="text-xs font-semibold uppercase tracking-wider text-charcoal">Unresolved commitments ({info.commitments.length})</p>
          <ul className="mt-1 space-y-0.5 text-xs text-charcoal">
            {info.commitments.map((c) => <li key={c.id} data-testid={`lifecycle-commitment-${c.id}`}>{c.area} · {c.ref} · <strong>{c.status}</strong>{c.dealer ? ` · ${c.dealer}` : ""}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ID-050 RC-CAT-001: archive / reactivate / guarded delete with an impact confirmation
export function LifecycleActions({ kind: baseKind, id: baseId, archived, label: baseLabel, onDone, testid }) {
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [override, setOverride] = useState(false);
  const [target, setTarget] = useState(null);
  const [choice, setChoice] = useState(null);
  const kind = target?.kind || baseKind;
  const id = target?.id || baseId;
  const label = target?.label || baseLabel;
  const open = async (m, t = null, keep = null) => {
    setOverride(false);
    setTarget(t);
    setChoice(keep);
    try { const { data } = await api.get(`/admin/catalog/${t?.kind || baseKind}/${t?.id || baseId}/removal-impact`); setInfo(data); setMode(m); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const run = async () => {
    setBusy(true);
    try {
      if (mode === "delete") await api.delete(`/admin/catalog/${kind}/${id}${override ? "?override_commitments=true" : ""}`);
      else await api.post(`/admin/catalog/${kind}/${id}/${mode}`);
      toast.success(mode === "delete" ? "Permanently deleted" : mode === "archive" ? "Archived — hidden from all ordering" : "Reactivated");
      setMode(null);
      resetDeletedBadgeCache();
      onDone?.(mode, kind);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || formatApiErrorDetail(d));
    } finally { setBusy(false); }
  };
  const last = mode === "delete" && baseKind === "variant" && !target ? info?.last_of_collection : null;
  const pending = !!last && !choice; // ID-075: last variation — founder must choose what happens to the collection
  const title = mode === "delete" && choice?.kind === "collection" ? `Delete ${baseLabel} and the ${choice.name} collection?`
    : pending ? `You are deleting the last variation in ${last.name}`
      : { archive: `Archive ${label}?`, reactivate: `Reactivate ${label}?`, delete: `Permanently delete ${label}?` }[mode];
  const deleteLabel = choice?.kind === "collection" ? `Delete ${baseLabel} and ${choice.name}` : choice?.kind === "variation" ? `Delete ${baseLabel} only` : "Delete permanently";
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {archived
        ? <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => open("reactivate")} data-testid={`${testid}-reactivate`}><RotateCcw className="h-3 w-3" /> Reactivate</Button>
        : <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => open("archive")} data-testid={`${testid}-archive`}><Archive className="h-3 w-3" /> Archive</Button>}
      <Button size="sm" variant="outline" className="h-8 gap-1 border-status-sold/40 text-xs text-status-sold" onClick={() => open("delete")} data-testid={`${testid}-delete`}><Trash2 className="h-3 w-3" /> Delete</Button>
      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent className="border-border bg-white sm:max-w-lg" data-testid="lifecycle-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-light text-charcoal">{title}</DialogTitle>
            <DialogDescription>
              {mode === "archive" && "It disappears from every catalog, browsing and new-order surface, and stale links can't order it. Past orders, quotes, receipts and inventory keep showing it as discontinued."}
              {mode === "reactivate" && "It returns to the catalog and ordering with its saved details and prices."}
              {mode === "delete" && !pending && "Deletion is permanent: it leaves every catalog, search, selector and inventory list, and its SKU and identifier can be reused. Past orders, quotes and receipts keep their original product details marked 'Product deleted'."}
            </DialogDescription>
          </DialogHeader>
          {info && <ImpactList info={info} />}
          {pending && (
            <div className="space-y-3 text-sm text-charcoal" data-testid="lifecycle-last-variation">
              <p>After deleting {baseLabel}, the {last.name} collection will contain no variations. What would you like to do?</p>
              <div className="rounded-md border border-border p-3">
                <p className="font-medium">Delete variation only</p>
                <p className="text-xs text-warmgrey">Permanently delete {baseLabel}. {last.name} stays in Mosaic Collections with 0 variations, keeps its settings, identifier and SKU prefix, and new variations can be added later.</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => setChoice({ kind: "variation", name: last.name })} data-testid="lifecycle-choice-variation-only">Delete {baseLabel} only</Button>
              </div>
              <div className="rounded-md border border-status-sold/40 p-3">
                <p className="font-medium">Delete variation and collection</p>
                <p className="text-xs text-warmgrey">Permanently delete {baseLabel} and {last.name}: its settings and catalog references are removed, the identifier and SKU prefix are released for reuse. Past transactions keep their snapshots.</p>
                <Button size="sm" className="mt-2 bg-status-sold text-white hover:opacity-90" data-testid="lifecycle-choice-delete-collection"
                  onClick={() => open("delete", { kind: "product", id: last.product_id, label: last.name }, { kind: "collection", name: last.name })}>Delete {baseLabel} and {last.name}</Button>
              </div>
              <Button variant="outline" onClick={() => setMode(null)} data-testid="lifecycle-choice-cancel">Cancel — make no changes</Button>
            </div>
          )}
          {mode === "delete" && info && !pending && !info.can_delete && (
            <div className="rounded-md bg-status-reserved/10 p-3 text-sm text-charcoal" data-testid="lifecycle-delete-blocked">
              <p>{info.delete_blocked_reason}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setMode(null)} data-testid="lifecycle-blocked-cancel">Cancel</Button>
                {!info.archived && <Button size="sm" className="bg-terracotta text-white hover:bg-terracotta-hover" onClick={() => setMode("archive")} data-testid="lifecycle-archive-instead">Archive instead</Button>}
              </div>
            </div>
          )}
          {mode === "delete" && !pending && info?.can_delete && info.needs_override && !override && (
            <div className="space-y-2 text-sm text-charcoal" data-testid="lifecycle-override-step">
              <p>Resolve these commitments first (cancel, fulfil or move them), or delete anyway.</p>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setMode(null)} data-testid="lifecycle-resolve-first">Resolve them first</Button>
                <Button className="bg-status-sold text-white hover:opacity-90" onClick={() => setOverride(true)} data-testid="lifecycle-delete-anyway">Delete anyway</Button>
              </div>
            </div>
          )}
          {mode === "delete" && override && (
            <p className="rounded-md border border-status-sold/40 bg-status-sold/5 p-3 text-sm text-charcoal" data-testid="lifecycle-override-warning">
              Final confirmation: the {info.commitments.length} commitment(s) above will NOT be cancelled, fulfilled or erased. They stay open with their original product details marked “Product deleted”, and you or the dealer must still resolve them. The product cannot be ordered, reserved or restocked again.
            </p>
          )}
          {!pending && !(mode === "delete" && info && (!info.can_delete || (info.needs_override && !override))) && (
            <div className="flex gap-2">
            <Button variant="outline" onClick={() => setMode(null)} data-testid="lifecycle-cancel">Cancel</Button>
            <Button disabled={busy} onClick={run} data-testid="lifecycle-confirm"
              className={mode === "delete" ? "bg-status-sold text-white hover:opacity-90" : "bg-terracotta text-white hover:bg-terracotta-hover"}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "delete" ? (override ? `${deleteLabel} — delete anyway` : deleteLabel) : mode === "archive" ? "Archive" : "Reactivate"}
            </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
